import React, { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db';
import { syncManager } from '../../services/sync';
import { Zap, ShieldCheck, Clock, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import HybridApprovalModal from '../../components/sovereign/HybridApprovalModal';
import ManualGuidanceChecklist from '../../components/sovereign/ManualGuidanceChecklist';

interface Workflow {
    id: string;
    mode: 'hybrid' | 'manual';
    notes?: string;
    externalId?: string;
    timestamp: string;
    [key: string]: any;
}

const SovereignExecutionHubView = () => {
    const workflows = useLiveQuery(() =>
        db.pendingWorkflows.where('status').equals('pending').toArray(),
        [],
    ) ?? [];

    const [activeModal, setActiveModal] = useState<{
        type: 'hybrid' | 'manual';
        data: any;
    } | null>(null);

    if (!workflows) {return <div className="p-8 text-center text-slate-400">Loading workflows...</div>;}

    const hybridTasks = workflows.filter((w: any) => w.mode === 'hybrid');
    const manualTasks = workflows.filter((w: any) => w.mode === 'manual');

    const handleApprove = async (payload: any) => {
        if (!activeModal) {return;}
        const workflow = activeModal.data;
        try {
            await syncManager.approveWorkflow(workflow.id, payload);
            setActiveModal(null);
        } catch (e: unknown) {
            alert(`Approval failed: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    const handleCompleteManual = async (notes: string) => {
        if (!activeModal) {return;}
        const workflow = activeModal.data;
        try {
            await syncManager.completeManualWorkflow(workflow.id, notes);
            setActiveModal(null);
        } catch (e: unknown) {
            alert(`Completion failed: ${e instanceof Error ? e.message : String(e)}`);
        }
    };

    const renderTaskCard = (workflow: any, mode: 'hybrid' | 'manual') => (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 hover:border-blue-200 transition-all cursor-pointer group shadow-sm hover:shadow-md active:scale-[0.98]">
            <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-xl ${mode === 'hybrid' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'}`}>
                    {mode === 'hybrid' ? <ShieldCheck size={20} /> : <Zap size={20} />}
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                    <Clock size={12} />
                    {new Date(workflow.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
            </div>
            <h4 className="text-slate-800 font-black text-sm uppercase tracking-tight mb-1">
                {workflow.notes || (mode === 'hybrid' ? 'Review Agent Draft' : 'Execute Manual Guidance')}
            </h4>
            <p className="text-slate-500 text-xs font-medium leading-relaxed mb-6 line-clamp-2">
                {workflow.externalId ? `WF-ID: ${workflow.externalId}` : 'Standard Orchestration Task'}
            </p>
            <div className="flex items-center justify-between">
                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-lg ${
                    mode === 'hybrid' ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                }`}>
                    {mode}
                </span>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setActiveModal({ type: mode, data: workflow });
                    }}
                    className="flex items-center gap-1 text-blue-600 text-xs font-black uppercase tracking-widest group-hover:translate-x-1 transition-transform"
                >
                    Review <ArrowRight size={14} />
                </button>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tighter">Sovereign Execution Hub</h2>
                    <p className="text-slate-500 font-medium">Manage Human-In-The-Loop orchestration tasks</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-slate-600 text-xs font-bold">
                    <CheckCircle2 size={16} className="text-emerald-500" />
                    {workflows.length} Pending Actions
                </div>
            </div>
            {workflows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 mb-4">
                        <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">All Clear</h3>
                    <p className="text-slate-500 font-medium max-w-xs mx-auto">
                        No pending HITL actions. The sovereign engine is running autonomously.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Hybrid Section */}
                    <section>
                        <div className="flex items-center gap-2 mb-4 pl-2">
                            <ShieldCheck size={20} className="text-blue-600" />
                            <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest">Hybrid Approvals</h3>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {hybridTasks.length === 0 && (
                                <div className="p-6 text-slate-400 text-xs font-medium italic text-center bg-slate-50 rounded-[2rem] border border-slate-100">
                                    No drafts requiring approval.
                                </div>
                            )}
                            {hybridTasks.map((t: any) => (
                                <div key={t.id} onClick={() => setActiveModal({ type: 'hybrid', data: t })}>
                                    {renderTaskCard(t, 'hybrid')}
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Manual Section */}
                    <section>
                        <div className="flex items-center gap-2 mb-4 pl-2">
                            <Zap size={20} className="text-emerald-600" />
                            <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest">Manual Guidance</h3>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {manualTasks.length === 0 && (
                                <div className="p-6 text-slate-400 text-xs font-medium italic text-center bg-slate-50 rounded-[2rem] border border-slate-100">
                                    No manual tasks pending.
                                </div>
                            )}
                            {manualTasks.map((t: any) => (
                                <div key={t.id} onClick={() => setActiveModal({ type: 'manual', data: t })}>
                                    {renderTaskCard(t, 'manual')}
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            )}

            {/* Modals */}
            {activeModal?.type === 'hybrid' && (
                <HybridApprovalModal
                    workflow={activeModal.data}
                    onApprove={handleApprove}
                    onClose={() => setActiveModal(null)}
                />
            )}
            {activeModal?.type === 'manual' && (
                <ManualGuidanceChecklist
                    workflow={activeModal.data}
                    onComplete={handleCompleteManual}
                    onClose={() => setActiveModal(null)}
                />
            )}
        </div>
    );
};

export default SovereignExecutionHubView;
